import React, { useState, useCallback } from 'react';
import styles from './MiniPlayer.module.scss';
import Skip from '../Icons/Skip';
import UpArrow from '../Icons/UpArrow';
import Close from '../Icons/Close';
import Play from '../Icons/Play';
import Pause from '../Icons/Pause';
import { useSongs } from '../../config/SongContext';

const extractVideoId = (url) => {
  const match = url?.match(
    /(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  return match ? match[1] : null;
};

const MiniPlayer = ({ song, onExpand, onClose, openSongPage, isPlaying, onPlayPause, progress = 0 }) => {
  const { getNextSong, setCurrentSong } = useSongs();
  const [isSkipping, setIsSkipping] = useState(false);

  const videoId = extractVideoId(song?.URL);
  const thumbnailUrl = videoId
    ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
    : null;

  const handleSkip = useCallback(async () => {
    if (isSkipping || !song?.id) return;
    setIsSkipping(true);
    try {
      const nextSong = await getNextSong(song.id);
      if (nextSong) {
        setCurrentSong(nextSong);
        openSongPage(nextSong.id);
      }
    } finally {
      setIsSkipping(false);
    }
  }, [song?.id, getNextSong, setCurrentSong, openSongPage, isSkipping]);

  return (
    <div className={styles.miniPlayer}>
      <div className={styles.progressBar} style={{ width: `${progress * 100}%` }} />
      <div className={styles.thumbnail}>
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={song?.songName}
            className={styles.thumbnailImg}
          />
        ) : (
          <div className={styles.thumbnailPlaceholder} />
        )}
        <button
          className={styles.playPauseOverlay}
          onClick={onPlayPause}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <Pause fill="#ffffff" width={16} height={16} />
          ) : (
            <Play fill="#ffffff" width={16} height={16} />
          )}
        </button>
      </div>

      <div className={styles.info}>
        <div className={styles.songName}>{song?.songName}</div>
        <div className={styles.artistName}>{song?.songArtist}</div>
      </div>

      <div className={styles.controls}>
        <button
          className={styles.controlButton}
          onClick={handleSkip}
          disabled={isSkipping}
          aria-label="Skip to next song"
        >
          <Skip fill="#404040" />
        </button>
        <button
          className={styles.controlButton}
          onClick={onExpand}
          aria-label="Expand player"
        >
          <UpArrow fill="#404040" />
        </button>
        <button
          className={styles.controlButton}
          onClick={onClose}
          aria-label="Close player"
        >
          <Close fill="#404040" />
        </button>
      </div>
    </div>
  );
};

export default MiniPlayer;
